import { useLocation } from "wouter";
import QuoteCarousel from "@/components/QuoteCarousel";
import { ArrowLeft, ExternalLink, AlertTriangle, BookOpen, CheckCircle, FileText, RotateCcw, TrendingUp, HelpCircle, Link2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const DISCLAIMER =
  "This page provides educational information about tax deductions for charitable contributions. It is not legal or tax advice. Tax rules vary based on individual circumstances. Consult a qualified tax professional or CPA before making financial decisions.";

const FAQ = [
  {
    q: "Is DebtBridge a registered 501(c)(3)?",
    a: "Yes. DebtBridge Foundation is a registered 501(c)(3) nonprofit organization. Your donations are tax-deductible to the extent allowed by law.",
  },
  {
    q: "Do I need a receipt for donations under $250?",
    a: "For cash donations under $250, a bank record or credit card statement is generally sufficient. For donations of $250 or more, you need a written acknowledgment from DebtBridge.",
  },
  {
    q: "What is the written acknowledgment I receive?",
    a: "After each completed donation, DebtBridge provides an immediate written acknowledgment confirming the amount, the date, and that no goods or services were provided in exchange.",
  },
  {
    q: "Can I deduct recurring monthly donations?",
    a: "Yes. Each recurring payment is deductible in the calendar year it is made. If you donate $50/month, you can deduct $600 for a full year of payments.",
  },
  {
    q: "What is the AGI limit for cash donations?",
    a: "For cash donations to public charities like DebtBridge, you can generally deduct up to 60% of your Adjusted Gross Income (AGI) in a given tax year.",
  },
  {
    q: "What if my total donations exceed the AGI limit?",
    a: "Any excess amount can be carried forward and deducted in the next five tax years, subject to the same AGI percentage limits each year.",
  },
  {
    q: "Do I receive anything in exchange for my donation?",
    a: "No. DebtBridge provides no goods or services in exchange for your donation. This means your entire contribution is eligible for a deduction (subject to AGI limits).",
  },
  {
    q: "How do I claim the deduction?",
    a: "To claim a charitable deduction, you must itemize deductions on Schedule A of your Form 1040. If you take the standard deduction, you generally cannot also claim a charitable deduction.",
  },
  {
    q: "Where can I download my year-end tax summary?",
    a: "Log into your Donor Portal and visit the 'My Donations' tab. Each completed donation has a downloadable tax certificate. An annual summary is also available.",
  },
  {
    q: "What EIN should I use for my tax forms?",
    a: "DebtBridge Foundation's EIN is 12-3456789. This appears on all tax certificates and acknowledgments we send you.",
  },
];

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, title, children }: SectionProps) {
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-green-800">
          <span className="flex-shrink-0 text-green-600">{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-3">
        {children}
      </CardContent>
    </Card>
  );
}

export default function TaxInfo() {
  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-bold">Tax Deduction Guide</span>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Disclaimer banner */}
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 mb-8">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-0.5">Educational Information Only</p>
            <p className="text-xs text-amber-700">{DISCLAIMER}</p>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-green-900 mb-2">Understanding Your Tax Deduction</h1>
        <p className="text-muted-foreground mb-8 text-base">
          Donations to DebtBridge Foundation are tax-deductible. Here's everything you need to know — in plain English.
        </p>

        {/* Section 1 */}
        <Section icon={<BookOpen className="h-5 w-5" />} title="How Your Donation Is Tax-Deductible">
          <p>
            Under <strong>IRS Section 170</strong>, individuals who make charitable contributions to qualifying nonprofit organizations can deduct those contributions from their federal taxable income. DebtBridge Foundation is a registered <strong>501(c)(3) public charity</strong>, which means donations to DebtBridge are eligible for this deduction.
          </p>
          <p>
            The deduction reduces the amount of your income that is subject to federal income tax. For example, if you're in the 22% tax bracket and donate $500, you may reduce your federal tax bill by approximately $110.
          </p>
          <p>
            To claim the deduction, you must <strong>itemize deductions</strong> on your tax return (Schedule A of Form 1040) rather than taking the standard deduction. Many donors find that bundling charitable giving into a single year makes itemizing worthwhile.
          </p>
        </Section>

        {/* Section 2 */}
        <Section icon={<CheckCircle className="h-5 w-5" />} title="What Qualifies">
          <p>Your donation to DebtBridge qualifies for a charitable deduction when:</p>
          <ul className="list-none space-y-2">
            {[
              "DebtBridge is a 501(c)(3) organization (it is — EIN: 12-3456789)",
              "Your donation is a cash contribution (credit card, bank transfer, or check)",
              "You receive no goods or services in exchange for your donation",
              "You make the donation in the tax year you wish to claim it",
              "You have proper documentation (receipt, bank record, or written acknowledgment)",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2">
            <strong>Note:</strong> DebtBridge does not provide any goods or services in exchange for donations, so 100% of your contribution is generally deductible (subject to AGI limits).
          </p>
        </Section>

        {/* Section 3 */}
        <Section icon={<FileText className="h-5 w-5" />} title="What You Receive from DebtBridge">
          <p>DebtBridge provides two types of documentation for your tax records:</p>
          <div className="space-y-3">
            <div className="rounded-lg border p-4 bg-green-50/50">
              <p className="font-semibold text-green-800 mb-1">1. Immediate Written Acknowledgment</p>
              <p>After each completed donation, you receive a confirmation that includes:</p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Donation amount</li>
                <li>Date of donation</li>
                <li>Name of organization (DebtBridge Foundation)</li>
                <li>EIN (12-3456789)</li>
                <li>Statement that no goods or services were provided in exchange</li>
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">This satisfies the IRS requirement for donations of $250 or more.</p>
            </div>
            <div className="rounded-lg border p-4 bg-green-50/50">
              <p className="font-semibold text-green-800 mb-1">2. Year-End Tax Summary</p>
              <p>
                At the end of each calendar year, a comprehensive summary of all your completed donations is available in your Donor Portal under "My Donations." Each donation also has a downloadable individual tax certificate.
              </p>
            </div>
          </div>
        </Section>

        {/* Section 4 */}
        <Section icon={<FileText className="h-5 w-5" />} title="How to File — Step by Step">
          <p>To claim your charitable deduction:</p>
          <ol className="list-none space-y-3">
            {[
              { step: "1", text: "Gather your documentation — download your DebtBridge tax certificates from the Donor Portal." },
              { step: "2", text: "Choose to itemize — on Form 1040, select 'itemized deductions' rather than the standard deduction." },
              { step: "3", text: "Complete Schedule A — enter your charitable contributions in Line 11 (cash contributions to public charities)." },
              { step: "4", text: "Enter total — add all your charitable donations from the tax year and enter the total on Line 11." },
              { step: "5", text: "Keep records — retain your tax certificates for at least 3 years in case of an IRS audit." },
            ].map((item) => (
              <li key={item.step} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center">
                  {item.step}
                </span>
                <span>{item.text}</span>
              </li>
            ))}
          </ol>
          <p className="mt-2">
            <strong>Tip:</strong> If you use tax software (TurboTax, H&R Block, etc.), look for the "Charitable Contributions" or "Donations" section — it will walk you through entering your DebtBridge donations.
          </p>
        </Section>

        {/* Section 5 */}
        <Section icon={<RotateCcw className="h-5 w-5" />} title="Recurring Donations">
          <p>
            If you set up a recurring monthly donation through DebtBridge, <strong>each payment is deductible in the calendar year it is processed</strong> — not when you signed up.
          </p>
          <p>
            For example, if you donate $100/month starting in February, you can deduct $1,100 for that calendar year (11 payments × $100). In subsequent years, you can deduct $1,200 (12 payments).
          </p>
          <p>
            Your year-end summary in the Donor Portal will show every completed payment and the exact dates, making it easy to calculate your annual deduction.
          </p>
        </Section>

        {/* Section 6 */}
        <Section icon={<TrendingUp className="h-5 w-5" />} title="Donation Limits and Carryforward Rules">
          <p>The IRS sets limits on how much you can deduct in a single tax year:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 border font-semibold">Donation Type</th>
                  <th className="text-left p-2 border font-semibold">AGI Limit</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border">Cash to public charity (like DebtBridge)</td>
                  <td className="p-2 border font-medium text-green-700">Up to 60% of AGI</td>
                </tr>
                <tr>
                  <td className="p-2 border">Appreciated property to public charity</td>
                  <td className="p-2 border">Up to 30% of AGI</td>
                </tr>
                <tr>
                  <td className="p-2 border">Cash to private foundation</td>
                  <td className="p-2 border">Up to 30% of AGI</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            <strong>Carryforward rule:</strong> If your total charitable contributions exceed the AGI limit, you can carry forward the unused portion for up to <strong>five subsequent tax years</strong>. Each carryforward year is subject to the same percentage limits.
          </p>
          <p className="text-xs">
            Example: If your AGI is $80,000 and you donate $60,000 to DebtBridge, you can deduct $48,000 (60% of AGI) in year one, and carry forward the remaining $12,000 for up to five years.
          </p>
        </Section>

        {/* Section 7 — FAQ */}
        <Section icon={<HelpCircle className="h-5 w-5" />} title="Frequently Asked Questions">
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="border rounded-lg p-4">
                <p className="font-semibold text-foreground mb-1">{item.q}</p>
                <p className="text-muted-foreground text-xs leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 8 — IRS Resources */}
        <Section icon={<Link2 className="h-5 w-5" />} title="IRS Resources">
          <p>For authoritative information, consult these official IRS resources:</p>
          <div className="space-y-2">
            {[
              {
                title: "IRS Publication 526 — Charitable Contributions",
                desc: "The primary IRS guide covering deductibility, limits, recordkeeping, and how to report contributions.",
                url: "https://www.irs.gov/publications/p526",
              },
              {
                title: "IRS Publication 561 — Determining the Value of Donated Property",
                desc: "Guidance on how to value non-cash contributions (not typically applicable to cash donations to DebtBridge).",
                url: "https://www.irs.gov/publications/p561",
              },
              {
                title: "IRS Tax Exempt Organization Search (501(c)(3) Lookup)",
                desc: "Verify that any organization you donate to is recognized as a 501(c)(3). Search for DebtBridge here.",
                url: "https://apps.irs.gov/app/eos/",
              },
              {
                title: "IRS Schedule A — Itemized Deductions",
                desc: "The form you need to claim your charitable deduction on your tax return.",
                url: "https://www.irs.gov/forms-pubs/about-schedule-a-form-1040",
              },
              {
                title: "IRS Topic 506 — Charitable Contributions",
                desc: "A quick overview of charitable contribution rules and requirements.",
                url: "https://www.irs.gov/taxtopics/tc506",
              },
            ].map((res) => (
              <a
                key={res.url}
                href={res.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
              >
                <ExternalLink className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5 group-hover:text-green-700" />
                <div>
                  <p className="font-medium text-green-800 group-hover:underline text-sm">{res.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{res.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </Section>

        {/* Bottom disclaimer */}
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 mt-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-0.5">Reminder</p>
            <p className="text-xs text-amber-700">{DISCLAIMER}</p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Button onClick={() => navigate("/donor")} className="mr-3">
            Back to Donor Portal
          </Button>
          <Button variant="outline" onClick={() => navigate("/")}>
            Return to Home
          </Button>
        </div>
      </div>
      <QuoteCarousel />

    </div>
  );
}
